import React, { useState, useRef, useEffect } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useNotifications } from '../../contexts/NotificationsContext';
import {
  LayoutDashboard, Car, Users, MapPin, Wrench, AlertTriangle,
  FileText, BarChart2, MessageSquare, Package, Shield, Bell,
  LogOut, Menu, X, ChevronDown, User, Mail, Send, Archive, History, Lock
} from 'lucide-react';

const navItems = [
  { to: '/', icon: LayoutDashboard, label: 'Dashboard', roles: ['admin','region_manager','branch_manager','accountant','office_worker'] },
  { to: '/vehicles', icon: Car, label: 'Pojazdy', roles: ['admin','region_manager','branch_manager','mechanic','driver','accountant','office_worker'] },
  { to: '/users', icon: Users, label: 'Pracownicy', roles: ['admin','region_manager','branch_manager','accountant','office_worker'] },
  { to: '/regions', icon: MapPin, label: 'Regiony i Oddziały', roles: ['admin','region_manager'] },
  { to: '/repairs', icon: Wrench, label: 'Naprawy', roles: ['admin','region_manager','branch_manager','mechanic','accountant'] },
  { to: '/faults', icon: AlertTriangle, label: 'Usterki', roles: ['admin','region_manager','branch_manager','mechanic','driver'] },
  { to: '/documents', icon: FileText, label: 'Dokumenty', roles: ['admin','region_manager','branch_manager','accountant','driver'] },
  { to: '/reports', icon: BarChart2, label: 'Raporty', roles: ['admin','region_manager','branch_manager','accountant'] },
  { to: '/messages', icon: MessageSquare, label: 'Komunikator', roles: ['admin','region_manager','branch_manager','mechanic','driver','accountant','office_worker'] },
  { to: '/equipment', icon: Package, label: 'Wyposażenie', roles: ['admin','region_manager','branch_manager','driver'] },
  { to: '/warehouse',   icon: Archive,  label: 'Magazyn',        roles: ['admin','region_manager'] },
  { to: '/history',     icon: History,  label: 'Historia',        roles: ['admin'] },
  { to: '/permissions', icon: Lock,     label: 'Uprawnienia',     roles: ['admin'] },
  { to: '/broadcast',   icon: Send,     label: 'Wysyłka masowa',  roles: ['admin'] },
  { to: '/audit',       icon: Shield,   label: 'Logi systemowe',  roles: ['admin'] },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const { unread, unreadMessages, clearMessageBadge, faultBellColor, resetFaultBellColor } = useNotifications();
  const navigate = useNavigate();

  // Na telefonie sidebar domyślnie zamknięty
  const isMobile = () => window.innerWidth < 768;
  const [sidebarOpen, setSidebarOpen] = useState(!isMobile());
  const [profileOpen, setProfileOpen] = useState(false);
  const [bellTooltip, setBellTooltip] = useState(false);
  const bellRef = useRef(null);

  // Zamknij sidebar na telefonie gdy zmienia się rozmiar okna
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) setSidebarOpen(false);
      else setSidebarOpen(true);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const visibleItems = navItems.filter(item => {
    if (item.roles.includes(user?.role)) return true;
    if (!user?.customPermissions || Object.keys(user.customPermissions).length === 0) return false;
    const key = item.to.replace('/','');
    return Object.keys(user.customPermissions).some(k => k.startsWith(key));
  });

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  // Zamknij dropdown profilu przy kliknięciu poza nim
  useEffect(() => {
    const handler = (e) => {
      if (profileOpen && !e.target.closest('[data-profile-menu]')) {
        setProfileOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [profileOpen]);

  const totalUnread = (unread || 0) + (unreadMessages || 0);
  const hasMessages = (unreadMessages || 0) > 0;
  const hasNotifs = (unread || 0) > 0;

  // Kliknięcie w link na telefonie zamyka sidebar
  const handleNavClick = (to) => {
    if (to === '/messages') clearMessageBadge();
    if (isMobile()) setSidebarOpen(false);
  };

  return (
    <div className="flex h-screen bg-gray-50 overflow-hidden">

      {/* Overlay na telefonie gdy sidebar otwarty */}
      {sidebarOpen && isMobile() && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-20"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        ${sidebarOpen ? 'w-64' : 'w-0 md:w-16'}
        bg-slate-900 text-white flex flex-col transition-all duration-200 flex-shrink-0
        fixed md:relative h-full z-30 overflow-hidden
      `}>
        {/* Logo */}
        <div className="flex items-center h-16 px-4 border-b border-slate-700 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="bg-blue-600 rounded-lg p-1.5 flex-shrink-0">
              <Car size={18} />
            </div>
            {sidebarOpen && <span className="font-bold text-lg tracking-tight whitespace-nowrap">CAR Fleet</span>}
          </div>
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="ml-auto text-slate-400 hover:text-white transition-colors flex-shrink-0"
          >
            {sidebarOpen ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>

        {/* Nav */}
        <nav className="flex-1 overflow-y-auto py-4 px-2">
          {visibleItems.map(item => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/'}
              onClick={() => handleNavClick(item.to)}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg mb-0.5 text-sm font-medium transition-colors ${
                  isActive
                    ? 'bg-blue-600 text-white'
                    : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                }`
              }
              title={!sidebarOpen ? item.label : undefined}
            >
              <div className="relative flex-shrink-0">
                <item.icon size={18} />
                {!sidebarOpen && item.to === '/messages' && hasMessages && (
                  <span className="absolute -top-1.5 -right-1.5 min-w-4 h-4 bg-yellow-400 text-slate-900 text-xs rounded-full flex items-center justify-center font-bold px-0.5 border border-slate-900 leading-none">
                    {unreadMessages > 9 ? '9+' : unreadMessages}
                  </span>
                )}
              </div>
              {sidebarOpen && <span className="flex-1 whitespace-nowrap">{item.label}</span>}
              {sidebarOpen && item.to === '/messages' && hasMessages && (
                <span className="bg-yellow-400 text-slate-900 text-xs rounded-full min-w-5 h-5 px-1.5 flex items-center justify-center flex-shrink-0 font-bold">
                  {unreadMessages > 99 ? '99+' : unreadMessages}
                </span>
              )}
              {sidebarOpen && item.to === '/notifications' && hasNotifs && (
                <span className="bg-red-500 text-white text-xs rounded-full min-w-5 h-5 px-1 flex items-center justify-center flex-shrink-0 font-semibold">
                  {unread > 99 ? '99+' : unread}
                </span>
              )}
            </NavLink>
          ))}
        </nav>

        {/* User footer */}
        <div className="border-t border-slate-700 p-3 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center text-sm font-bold flex-shrink-0">
              {user?.firstName?.[0]}{user?.lastName?.[0]}
            </div>
            {sidebarOpen && (
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">{user?.firstName} {user?.lastName}</div>
                <div className="text-xs text-slate-400 truncate">{user?.roleLabel}</div>
              </div>
            )}
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Top bar */}
        <header className="bg-white border-b border-gray-200 px-3 md:px-6 h-16 flex items-center justify-between flex-shrink-0">

          {/* Hamburger na telefonie (lewa strona) */}
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="md:hidden p-2 rounded-lg text-gray-500 hover:bg-gray-100 transition-colors"
          >
            <Menu size={22} />
          </button>

          <h1 className="text-lg font-semibold text-gray-800 hidden md:block" />

          <div className="flex items-center gap-1 md:gap-2 ml-auto">

            {/* Dzwoneczek */}
            <div className="relative" ref={bellRef}>
              <button
                onMouseEnter={() => totalUnread > 0 && setBellTooltip(true)}
                onMouseLeave={() => setBellTooltip(false)}
                onClick={() => {
                  setBellTooltip(false);
                  if (resetFaultBellColor) resetFaultBellColor();
                  if (hasMessages) {
                    clearMessageBadge();
                    navigate('/messages');
                  } else {
                    navigate('/notifications');
                  }
                }}
                className={`relative p-2 rounded-lg transition-all ${
                  hasMessages
                    ? 'text-yellow-500 hover:bg-yellow-50 animate-pulse'
                    : hasNotifs
                    ? 'text-red-500 hover:bg-red-50 animate-pulse'
                    : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100'
                }`}
              >
                <Bell size={20} className={
                  hasMessages ? 'text-yellow-500' :
                  faultBellColor === 'red' ? 'text-red-500 animate-pulse' :
                  faultBellColor === 'orange' ? 'text-orange-500 animate-pulse' :
                  faultBellColor === 'blue' ? 'text-blue-500 animate-pulse' :
                  hasNotifs ? 'text-red-500' : ''
                } />
                {totalUnread > 0 && (
                  <span className={`absolute top-0.5 right-0.5 w-4 h-4 text-white text-xs rounded-full flex items-center justify-center font-bold leading-none ${
                    hasMessages ? 'bg-yellow-500' :
                    faultBellColor === 'red' ? 'bg-red-500' :
                    faultBellColor === 'orange' ? 'bg-orange-500' :
                    faultBellColor === 'blue' ? 'bg-blue-500' : 'bg-red-500'
                  }`}>
                    {totalUnread > 9 ? '9+' : totalUnread}
                  </span>
                )}
              </button>

              {bellTooltip && totalUnread > 0 && (
                <div className="absolute right-0 top-full mt-2 z-50 w-64 bg-gray-900 text-white text-sm rounded-xl shadow-xl p-3 pointer-events-none">
                  <div className={`font-semibold mb-2 ${hasMessages ? 'text-yellow-300' : 'text-red-300'}`}>
                    {hasMessages ? '💬 Masz nowe wiadomości' : '🔔 Masz powiadomienia'}
                  </div>
                  {hasMessages && (
                    <div className="flex items-center gap-2 py-1.5 border-b border-gray-700">
                      <Mail size={14} className="text-yellow-400 flex-shrink-0" />
                      <span className="text-gray-200"><span className="font-bold text-white">{unreadMessages}</span> nowych wiadomości</span>
                    </div>
                  )}
                  {hasNotifs && (
                    <div className="flex items-center gap-2 py-1.5">
                      <Bell size={14} className="text-red-400 flex-shrink-0" />
                      <span className="text-gray-200"><span className="font-bold text-white">{unread}</span> nowych powiadomień</span>
                    </div>
                  )}
                  <div className="mt-2 text-xs text-gray-400">
                    {hasMessages ? 'Kliknij → przejdź do komunikatora' : 'Kliknij → przejdź do powiadomień'}
                  </div>
                  <div className="absolute -top-1.5 right-4 w-3 h-3 bg-gray-900 rotate-45" />
                </div>
              )}
            </div>

            {/* Profile dropdown */}
            <div className="relative" data-profile-menu>
              <button
                onClick={() => setProfileOpen(!profileOpen)}
                className="flex items-center gap-1 md:gap-2 px-2 md:px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors text-sm"
              >
                <div className="w-7 h-7 rounded-full bg-blue-600 text-white flex items-center justify-center text-xs font-bold">
                  {user?.firstName?.[0]}{user?.lastName?.[0]}
                </div>
                <span className="hidden md:block font-medium text-gray-700">{user?.firstName}</span>
                <ChevronDown size={14} className="text-gray-400" />
              </button>

              {profileOpen && (
                <div className="absolute right-0 top-full mt-1 w-48 bg-white rounded-xl shadow-lg border border-gray-200 py-1 z-50">
                  <NavLink to="/profile" onClick={() => setProfileOpen(false)} className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">
                    <User size={15} /> Mój profil
                  </NavLink>
                  <hr className="my-1" />
                  <button onClick={handleLogout} className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50">
                    <LogOut size={15} /> Wyloguj
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Page content — przewijalne, padding mniejszy na telefonie */}
        <main className="flex-1 overflow-y-auto p-3 md:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
